import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { AgentThreadRecord } from "@/shared/domain/agentThread";

import styles from "./adminTable.module.css";

interface AgentThreadsTableProps {
  agentThreads: AgentThreadRecord[];
  isLoading: boolean;
  selectedAgentThreadId: string | undefined;
  onSelectAgentThread: (threadId: string) => void;
  feedbackCountsByAgentThread?: Record<
    string,
    { positive: number; negative: number } | undefined
  >;
  onOpenFeedbackForAgentThread?: (threadId: string) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  currentPage: number;
}

export function AgentThreadsTable({
  agentThreads,
  isLoading,
  selectedAgentThreadId,
  onSelectAgentThread,
  feedbackCountsByAgentThread,
  onOpenFeedbackForAgentThread,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  currentPage,
}: AgentThreadsTableProps) {
  const theme = useTheme();

  if (isLoading && agentThreads.length === 0) {
    return (
      <div className={styles.emptyState}>
        <CircularProgress size="1.5rem" />
      </div>
    );
  }

  if (agentThreads.length === 0) {
    return (
      <div className={styles.emptyState}>
        {hasPreviousPage
          ? "No agentThreads on this page. Use Previous to go back."
          : "No agentThreads found."}
      </div>
    );
  }

  return (
    <div>
      <TableContainer className={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Session ID</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Org ID</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Feedback</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agentThreads.map((agentThread) => {
              const isSelected =
                agentThread.thread_id === selectedAgentThreadId;
              const counts =
                feedbackCountsByAgentThread?.[agentThread.thread_id];
              return (
                <TableRow
                  key={agentThread.thread_id}
                  onClick={() => onSelectAgentThread(agentThread.thread_id)}
                  className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""}`}
                >
                  <TableCell className={styles.cellTruncate}>
                    {agentThread.title || "Untitled"}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {agentThread.thread_id}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {agentThread.created_by}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {agentThread.org_id}
                  </TableCell>
                  <TableCell>
                    {new Date(agentThread.created).toLocaleString()}
                  </TableCell>
                  <TableCell>{agentThread.model_profile || "-"}</TableCell>
                  <TableCell>
                    {counts ? (
                      <span
                        role={
                          onOpenFeedbackForAgentThread ? "button" : undefined
                        }
                        tabIndex={onOpenFeedbackForAgentThread ? 0 : undefined}
                        className={styles.feedbackCounts}
                        onClick={(e) => {
                          if (!onOpenFeedbackForAgentThread) {
                            return;
                          }
                          e.stopPropagation();
                          onOpenFeedbackForAgentThread(agentThread.thread_id);
                        }}
                        onKeyDown={(e) => {
                          if (!onOpenFeedbackForAgentThread) {
                            return;
                          }
                          if (e.key === "Enter" || e.key === " ") {
                            // ``preventDefault`` so Space doesn't also
                            // scroll the page (the default behaviour for
                            // Space on a focusable element).
                            e.preventDefault();
                            e.stopPropagation();
                            onOpenFeedbackForAgentThread(agentThread.thread_id);
                          }
                        }}
                      >
                        <ThumbUpAltIcon
                          fontSize="inherit"
                          sx={{ color: theme.palette.success.main }}
                        />{" "}
                        {counts.positive}{" "}
                        <ThumbDownAltIcon
                          fontSize="inherit"
                          sx={{ color: theme.palette.error.main }}
                        />{" "}
                        {counts.negative}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={styles.paginationContainer}>
        <span className={styles.pageIndicator}>Page {currentPage + 1}</span>
        <div className={styles.paginationButtons}>
          <IconButton
            size="small"
            onClick={onPreviousPage}
            disabled={!hasPreviousPage || isLoading}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNextPage}
            disabled={!hasNextPage || isLoading}
            aria-label="Next page"
          >
            <NavigateNextIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
